import React, { useState } from 'react';
import { Binary, Copy, Trash2, ArrowLeftRight } from 'lucide-react';

const BinaryDecimalTool = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('binaryToDecimal'); // binaryToDecimal, decimalToBinary
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const convert = () => {
    setError('');
    setOutput('');

    if (!input.trim()) {
      setError('Please enter a value');
      return;
    }

    try {
      if (mode === 'binaryToDecimal') {
        // Validate binary input
        if (!/^[01\s]+$/.test(input)) {
          setError('Invalid binary number. Only 0 and 1 are allowed.');
          return;
        }
        const cleanBinary = input.replace(/\s/g, '');
        const decimal = parseInt(cleanBinary, 2);
        setOutput(decimal.toString());
      } else {
        // Validate decimal input
        const num = parseInt(input, 10);
        if (isNaN(num) || num < 0) {
          setError('Please enter a valid positive integer');
          return;
        }
        setOutput(num.toString(2));
      }
    } catch (e) {
      setError('Conversion error: ' + e.message);
    }
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const swapMode = () => {
    setMode(mode === 'binaryToDecimal' ? 'decimalToBinary' : 'binaryToDecimal');
    // Swap input/output if output exists
    if (output) {
      setInput(output);
      setOutput('');
    }
  };

  const examples = mode === 'binaryToDecimal' 
    ? ['1010', '11111111', '10000000', '1100100', '11110001001000000']
    : ['10', '255', '128', '100', '123456'];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 p-4 bg-gray-800 rounded-lg">
        <div className={`flex-1 text-center ${mode === 'binaryToDecimal' ? 'text-blue-400' : 'text-gray-400'}`}>
          <div className="text-sm font-medium">Binary</div>
          <div className="text-xs text-gray-500">Base 2</div>
        </div>
        <button
          onClick={swapMode}
          className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
          title="Swap direction"
        >
          <ArrowLeftRight size={20} />
        </button>
        <div className={`flex-1 text-center ${mode === 'decimalToBinary' ? 'text-blue-400' : 'text-gray-400'}`}>
          <div className="text-sm font-medium">Decimal</div>
          <div className="text-xs text-gray-500">Base 10</div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {mode === 'binaryToDecimal' ? 'Binary Number' : 'Decimal Number'}
        </label>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={mode === 'binaryToDecimal' ? '10101010' : '170'}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white font-mono text-lg placeholder-gray-500 focus:border-blue-500"
          onKeyDown={(e) => e.key === 'Enter' && convert()}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-500">Examples:</span>
        {examples.map((ex) => (
          <button
            key={ex}
            onClick={() => setInput(ex)}
            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm text-gray-300 font-mono"
          >
            {ex}
          </button>
        ))}
      </div>

      <button
        onClick={convert}
        disabled={!input.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <Binary size={18} />
        Convert
      </button>

      {output && (
        <div className="p-4 bg-gray-800 rounded-lg">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {mode === 'binaryToDecimal' ? 'Decimal Result' : 'Binary Result'}
          </label>
          <div className="flex items-center justify-between">
            <code className="text-2xl font-mono text-green-400 break-all">{output}</code>
            <button
              onClick={copyOutput}
              className={`p-2 rounded transition-colors ${
                copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              <Copy size={18} />
            </button>
          </div>
          {mode === 'binaryToDecimal' && (
            <div className="mt-3 text-sm text-gray-500">
              Hex: <span className="text-purple-400 font-mono">{parseInt(output).toString(16).toUpperCase()}</span>
              {' | '}
              Octal: <span className="text-yellow-400 font-mono">{parseInt(output).toString(8)}</span>
            </div>
          )}
          {mode === 'decimalToBinary' && output.length > 4 && (
            <div className="mt-3 text-sm text-gray-500">
              Formatted: <span className="text-blue-400 font-mono">{output.match(/.{1,4}/g)?.join(' ')}</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="p-4 bg-gray-800/50 rounded-lg">
        <h3 className="text-sm font-medium text-gray-300 mb-2">About Binary Numbers</h3>
        <p className="text-sm text-gray-400">
          Binary (base-2) uses only digits 0 and 1. Each position represents a power of 2.
          For example, binary <code className="text-blue-400">1010</code> = 1×8 + 0×4 + 1×2 + 0×1 = <code className="text-green-400">10</code> in decimal.
        </p>
      </div>

      <button
        onClick={() => { setInput(''); setOutput(''); setError(''); }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
      >
        <Trash2 size={16} />
        Clear
      </button>
    </div>
  );
};

export default BinaryDecimalTool;
